/**
 * Backend API base for routes on the Arcadia Node server (`/backend`).
 * Production: set `VITE_API_URL` to the public API origin (e.g. `https://api.example.com`).
 * Local dev: leave unset so requests stay same-origin and Vite proxies `/api` to the API.
 */
export function getApiBase(): string {
  const raw = import.meta.env.VITE_API_URL as string | undefined;
  return (raw || '').replace(/\/$/, '');
}

export function apiUrl(path: string): string {
  const base = getApiBase();
  const p = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${p}` : p;
}

/**
 * WebSocket URL for merged-backend realtime (`/realtime` on the same host as the HTTP API).
 * Honors `VITE_REALTIME_URL` when set (http(s) or ws(s)).
 */
export function getRealtimeWebSocketUrl(): string {
  const explicit = (import.meta.env.VITE_REALTIME_URL as string | undefined)?.trim();
  if (explicit) {
    const e = explicit.replace(/\/$/, '');
    if (e.startsWith("ws://") || e.startsWith("wss://")) {
      return e.endsWith("/realtime") ? e : `${e}/realtime`;
    }
    const protocol = e.startsWith("https") ? "wss:" : "ws:";
    const host = e.replace(/^https?:\/\//, "");
    return `${protocol}//${host}/realtime`;
  }

  let httpBase = getApiBase();
  if (!httpBase && typeof window !== "undefined") {
    httpBase = window.location.origin;
  }
  if (!httpBase) {
    throw new Error(
      "Realtime: set VITE_API_URL (production build) or VITE_REALTIME_URL, or load the app in the browser with the dev proxy."
    );
  }
  const protocol = httpBase.startsWith("https") ? "wss:" : "ws:";
  const host = httpBase.replace(/^https?:\/\//, "").replace(/\/$/, "");
  return `${protocol}//${host}/realtime`;
}
